import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { HomeIcon, FileTextIcon, XIcon, UsersIcon, ShieldIcon, ClipboardListIcon, ChevronDownIcon, ChevronRightIcon, BarChartIcon, SettingsIcon, AlertTriangleIcon, CreditCardIcon } from 'lucide-react';
interface SidebarProps {
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}
const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  setIsOpen
}) => {
  const [expandedMenus, setExpandedMenus] = useState<Record<string, boolean>>({
    reports: true,
    administration: false
  });
  const toggleMenu = (menuKey: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };
  return <>
      {/* Mobile sidebar */}
      <div className={`fixed inset-0 z-40 md:hidden ${isOpen ? 'block' : 'hidden'}`} data-id="element-82">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity" aria-hidden="true" onClick={() => setIsOpen(false)} data-id="element-83"></div>
        <div className="fixed inset-y-0 left-0 flex max-w-xs w-full bg-white shadow-xl transform transition-transform duration-300 ease-in-out" data-id="element-84">
          <div className="h-full flex flex-col w-full" data-id="element-85">
            <div className="flex items-center justify-between h-16 px-4 border-b border-gray-200 bg-gray-50" data-id="element-86">
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent" data-id="element-87">
                Enterprise Portal
              </h2>
              <button type="button" className="rounded-md p-1 text-gray-400 hover:text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500" onClick={() => setIsOpen(false)} data-id="element-88">
                <span className="sr-only" data-id="element-89">Close sidebar</span>
                <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-90" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto pt-5 pb-4 px-2" data-id="element-91">
              <nav className="flex-1 space-y-1" data-id="element-92">
                <SidebarContent expandedMenus={expandedMenus} toggleMenu={toggleMenu} data-id="element-93" />
              </nav>
            </div>
            <div className="flex-shrink-0 p-4 border-t border-gray-200" data-id="element-94">
              <div className="flex items-center" data-id="element-95">
                <div className="flex-shrink-0" data-id="element-96">
                  <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center text-white font-semibold" data-id="element-97">
                    A
                  </div>
                </div>
                <div className="ml-3" data-id="element-98">
                  <p className="text-sm font-medium text-gray-900" data-id="element-99">
                    Admin User
                  </p>
                  <p className="text-xs font-medium text-gray-500" data-id="element-100">
                    View profile
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Desktop sidebar */}
      <div className="hidden md:flex md:flex-shrink-0" data-id="element-101">
        <div className="flex flex-col w-64" data-id="element-102">
          <div className="flex flex-col h-0 flex-1 bg-white border-r border-gray-200 shadow-sm" data-id="element-103">
            <div className="flex items-center h-16 flex-shrink-0 px-4 border-b border-gray-200 bg-gray-50" data-id="element-104">
              <h2 className="text-xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 bg-clip-text text-transparent" data-id="element-105">
                Enterprise Portal
              </h2>
            </div>
            <div className="flex-1 flex flex-col overflow-y-auto" data-id="element-106">
              <div className="flex-1 py-6 px-4" data-id="element-107">
                <nav className="flex-1 space-y-2" data-id="element-108">
                  <SidebarContent expandedMenus={expandedMenus} toggleMenu={toggleMenu} data-id="element-109" />
                </nav>
              </div>
              <div className="flex-shrink-0 p-4 border-t border-gray-200" data-id="element-110">
                <div className="flex items-center" data-id="element-111">
                  <div className="flex-shrink-0" data-id="element-112">
                    <div className="h-10 w-10 rounded-full bg-gradient-to-r from-blue-600 to-blue-800 flex items-center justify-center text-white font-semibold" data-id="element-113">
                      A
                    </div>
                  </div>
                  <div className="ml-3" data-id="element-114">
                    <p className="text-sm font-medium text-gray-900" data-id="element-115">
                      Admin User
                    </p>
                    <p className="text-xs font-medium text-gray-500" data-id="element-116">
                      View profile
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>;
};
interface SidebarContentProps {
  expandedMenus: Record<string, boolean>;
  toggleMenu: (key: string) => void;
}
function SidebarContent({
  expandedMenus,
  toggleMenu
}: SidebarContentProps) {
  return <>
      {/* Dashboard */}
      <NavLink to="/" className={({
      isActive
    }) => `group flex items-center px-3 py-2.5 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900'}`} end data-id="element-117">
        <HomeIcon className="mr-3 flex-shrink-0 h-5 w-5 text-gray-500 group-hover:text-blue-600" data-id="element-118" />
        Dashboard
      </NavLink>

      {/* Administration Section */}
      <div className="space-y-1" data-id="element-119">
        <button type="button" className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-gray-900 focus:outline-none transition-colors duration-150" onClick={() => toggleMenu('administration')} data-id="element-120">
          <div className="flex items-center" data-id="element-121">
            <ShieldIcon className="mr-3 flex-shrink-0 h-5 w-5 text-gray-500" data-id="element-122" />
            <span data-id="element-123">Administration</span>
          </div>
          <div className="w-5 h-5 flex items-center justify-center" data-id="element-124">
            {expandedMenus.administration ? <ChevronDownIcon className="h-4 w-4 text-gray-500" data-id="element-125" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500" data-id="element-126" />}
          </div>
        </button>
        <div className={`space-y-1 pl-10 overflow-hidden transition-all duration-200 ${expandedMenus.administration ? 'max-h-40' : 'max-h-0'}`} data-id="element-127">
          <NavLink to="/admin/users" className={({
          isActive
        }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-128">
            <div className="flex items-center" data-id="element-129">
              <UsersIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-130" />
              User Management
            </div>
          </NavLink>
          <NavLink to="/admin/roles" className={({
          isActive
        }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-131">
            <div className="flex items-center" data-id="element-132">
              <ShieldIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-133" />
              Role Management
            </div>
          </NavLink>
        </div>
      </div>

      {/* Reports Section */}
      <div className="space-y-1" data-id="element-134">
        <button type="button" className="flex items-center justify-between w-full px-3 py-2.5 text-sm font-medium text-gray-700 rounded-md hover:bg-gray-50 hover:text-gray-900 focus:outline-none transition-colors duration-150" onClick={() => toggleMenu('reports')} data-id="element-135">
          <div className="flex items-center" data-id="element-136">
            <FileTextIcon className="mr-3 flex-shrink-0 h-5 w-5 text-gray-500" data-id="element-137" />
            <span data-id="element-138">Reports</span>
          </div>
          <div className="w-5 h-5 flex items-center justify-center" data-id="element-139">
            {expandedMenus.reports ? <ChevronDownIcon className="h-4 w-4 text-gray-500" data-id="element-140" /> : <ChevronRightIcon className="h-4 w-4 text-gray-500" data-id="element-141" />}
          </div>
        </button>
        <div className={`space-y-1 pl-10 overflow-hidden transition-all duration-200 ${expandedMenus.reports ? 'max-h-60' : 'max-h-0'}`} data-id="element-142">
          <NavLink to="/reports/merchant-registration" className={({
          isActive
        }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-143">
            <div className="flex items-center" data-id="element-144">
              <UsersIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-145" />
              Merchant Registration
            </div>
          </NavLink>
          <NavLink to="/reports/merchant-transaction" className={({
          isActive
        }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-146">
            <div className="flex items-center" data-id="element-147">
              <CreditCardIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-148" />
              Merchant Transaction
            </div>
          </NavLink>
          <NavLink to="/reports/transaction-summary" className={({
          isActive
        }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-149">
            <div className="flex items-center" data-id="element-150">
              <BarChartIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-151" />
              Transaction Summary
            </div>
          </NavLink>
          <NavLink to="/reports/audit-logs" className={({
          isActive
        }) => `block px-3 py-2 text-sm font-medium rounded-md transition-colors duration-150 ${isActive ? 'bg-blue-50 text-blue-700' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'}`} data-id="element-152">
            <div className="flex items-center" data-id="element-153">
              <ClipboardListIcon className="mr-3 h-4 w-4 text-gray-500" data-id="element-154" />
              Audit Logs
            </div>
          </NavLink>
        </div>
      </div>
    </>;
}
export default Sidebar;